import {
  $isTextNode,
  type DOMConversionMap,
  type DOMConversionOutput,
  type TextFormatType,
  type TextNode,
} from "lexical";

const STYLE_PROPS = [
  "background-color",
  "color",
  "font-family",
  "font-size",
] as const;

const TAG_FORMAT: Partial<Record<string, TextFormatType>> = {
  code: "code",
  em: "italic",
  i: "italic",
  b: "bold",
  strong: "bold",
  sub: "subscript",
  sup: "superscript",
  u: "underline",
  s: "strikethrough",
};

function getPreservedStyle(element: HTMLElement): string {
  return STYLE_PROPS.map((prop) => {
    const value = element.style.getPropertyValue(prop);
    return value ? `${prop}: ${value}` : null;
  })
    .filter((value): value is string => value != null)
    .join("; ");
}

function applyFormatFromStyle(
  node: TextNode,
  style: CSSStyleDeclaration,
): TextNode {
  let next = node;
  const fontWeight = style.fontWeight;
  const textDecoration = style.textDecoration.split(" ");
  const toggle = (format: TextFormatType) => {
    if (!next.hasFormat(format)) {
      next = next.toggleFormat(format);
    }
  };

  if (fontWeight === "700" || fontWeight === "bold") toggle("bold");
  if (style.fontStyle === "italic") toggle("italic");
  if (textDecoration.includes("underline")) toggle("underline");
  if (textDecoration.includes("line-through")) toggle("strikethrough");
  if (style.verticalAlign === "sub") toggle("subscript");
  if (style.verticalAlign === "super") toggle("superscript");
  return next;
}

function convertStyledElement(element: HTMLElement): DOMConversionOutput {
  const preservedStyle = getPreservedStyle(element);
  const tagFormat = TAG_FORMAT[element.nodeName.toLowerCase()];
  return {
    node: null,
    forChild: (lexicalNode) => {
      if (!$isTextNode(lexicalNode)) return lexicalNode;
      let next = applyFormatFromStyle(lexicalNode, element.style);
      if (tagFormat && !next.hasFormat(tagFormat)) {
        next = next.toggleFormat(tagFormat);
      }
      if (preservedStyle) {
        const existing = next.getStyle().trim();
        next = next.setStyle(
          existing
            ? `${existing}${existing.endsWith(";") ? "" : ";"} ${preservedStyle}`
            : preservedStyle,
        );
      }
      return next;
    },
  };
}

function styledImport() {
  return () => ({
    conversion: convertStyledElement,
    priority: 1 as const,
  });
}

/** HTML import map that keeps inline styles (e.g. background-color). */
export const htmlStyleImport: DOMConversionMap = {
  b: styledImport(),
  code: styledImport(),
  em: styledImport(),
  i: styledImport(),
  s: styledImport(),
  span: styledImport(),
  strong: styledImport(),
  sub: styledImport(),
  sup: styledImport(),
  u: styledImport(),
};
